import { useState, useEffect, useMemo, useRef } from 'react';
import Modal from '../components/Modal';
import PaymentEmptyState from '../components/PaymentEmptyState';
import InlineLoadError from '../components/InlineLoadError';
import { useNavigate, useLocation } from '../routing';
import { suppliersApi, documentsApi, formatCurrencyWhole } from '../utils/api';
import { PaymentLifecycleBadge, PaymentLifecycleControl, LifecycleHelp } from '../components/PaymentLifecycleBadge';
import PaymentTrackingHelp from '../components/PaymentTrackingHelp';
import { isOpenTabState, isPaidTabState, approvalOfferForRange } from '../utils/paymentLifecycle';
import { toast } from '../components/Toast';
import { track, EVENTS } from '../utils/analytics';
import CommentsModal from '../components/CommentsModal';
import PaymentModal from '../components/PaymentModal';
import MultiPaymentModal from '../components/MultiPaymentModal';
import PaymentHistoryModal from '../components/PaymentHistoryModal';
import SuppliersFilterBar from '../components/SuppliersFilterBar';
import ListRow from '../components/ListRow';
import FilterSheet from '../components/FilterSheet';
import PaymentViewTabs, { consumeViewEntry } from '../components/PaymentViewTabs';
import { useIsMobile } from '../utils/useIsMobile';
import { useAuth } from '../context/AuthContext';
import { useSettings } from '../context/SettingsContext';
import { useSortable } from '../utils/useSortable';
import { readSharedDateRange, writeSharedDateRange } from '../utils/sharedDateRange';
import { setSupplierJump, takeSupplierJump } from '../utils/supplierJump';
import { paymentMethodIcon, PaymentTermsInput, PAYMENT_METHOD_OPTIONS } from './SupplierManagementPage';
import { requiredInvoiceMonth } from '../utils/dueDate';
import { roundWhole } from '../utils/money';
import { currentMonthRange, periodLabel, PRESETS_MONTHLY, toStoredPeriod, fromStoredPeriod } from '../utils/period';
import { usePresetPeriod } from '../utils/usePresetPeriod';
import Icon from '../components/Icon';
import { PURCHASE_INFO } from '../components/StatInfo';
import StatCardsRow from '../components/StatCardsRow';
import NewSupplierOptions from '../components/NewSupplierOptions';
import HqOnlyNotice, { HqOnlyLock } from '../components/HqOnlyNotice';
import { ViewAsMenuItems } from '../components/ViewAsMenu';
import LedgerMark from '../components/LedgerMark';
import LedgerViewer from '../components/LedgerViewer';
import { ledgerCoversWindow } from '../utils/ledgerCoverage';

// Robust clipboard copy: try the async Clipboard API first (works on
// secure contexts with a user gesture), and fall back to a hidden
// textarea + execCommand('copy') when the API is blocked or rejects
// (some browsers/permission states reject even on localhost).
function copyToClipboard(text) {
  if (navigator.clipboard && window.isSecureContext) {
    return navigator.clipboard.writeText(text).catch(() => legacyCopy(text));
  }
  return legacyCopy(text);
}

function legacyCopy(text) {
  return new Promise((resolve, reject) => {
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.focus();
      ta.select();
      const ok = document.execCommand('copy');
      document.body.removeChild(ta);
      ok ? resolve() : reject(new Error('execCommand failed'));
    } catch (e) {
      reject(e);
    }
  });
}

// ─── Payment due-date calculation ─────────────────────────────────────────────
// "שוטף X" from month M: go to month M+1 and add X days treating every month
// as 30 days. Day 30 normalises to the 1st of the following month.
// "שוטף" (no number) = x=1 = 1st of the following month (e.g. March → 1.4)
// Examples from March: שוטף→1/4, שוטף 15→15/4, שוטף 30→1/5, שוטף 45→15/5
function calcDueDate(paymentTerms, lastDocDate) {
  if (!paymentTerms?.startsWith('שוטף') || !lastDocDate) return null;
  const raw = parseInt(paymentTerms.replace('שוטף', '').trim(), 10);
  const x   = isNaN(raw) || raw <= 0 ? 1 : raw; // bare "שוטף" = 1st of next month

  const parts = lastDocDate.split('/');
  if (parts.length !== 3) return null;
  let month = parseInt(parts[1], 10) + 1; // one month after invoice month
  let year  = parseInt(parts[2], 10);
  if (month > 12) { month = 1; year += 1; }

  let day = x;
  // Overflow: each 30 days → advance one month
  while (day > 30) {
    day -= 30;
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  // Day 30 = "end of month" → normalise to 1st of next month
  if (day === 30) {
    day = 1;
    month += 1;
    if (month > 12) { month = 1; year += 1; }
  }
  return new Date(year, month - 1, day);
}

function formatDueDate(date) {
  if (!date) return null;
  const dd = String(date.getDate()).padStart(2, '0');
  const mm = String(date.getMonth() + 1).padStart(2, '0');
  const yy = String(date.getFullYear()).slice(-2);
  return `${dd}/${mm}/${yy}`;
}

// 'YYYY-MM-DD' → 'dd/mm/yy' (matches formatDueDate's output format).
function fmtIsoToDisplay(iso) {
  if (!iso) return null;
  const [y, m, d] = iso.split('-');
  return `${d}/${m}/${y.slice(-2)}`;
}

function dueDateStatus(date) {
  if (!date) return null;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const diff = Math.round((date - today) / 86400000);
  if (diff < 0)  return 'overdue';   // red
  if (diff <= 7) return 'soon';      // orange
  return 'ok';
}

// The authoritative full total (VAT-incl.) for a supplier row. Falls back to
// unpaid+paid only if total_amount is ever absent — never total_amount+paid,
// which would double-count when amount_unpaid is null.
function supplierGross(s) {
  return s.total_amount ?? ((s.amount_unpaid ?? 0) + (s.amount_paid ?? 0));
}

// Sticky filters: the suppliers filter bar persists across navigation (entering
// a supplier and coming back, reloading the tab) and only resets on an explicit
// "clear all" or when the browser/tab is closed. We use sessionStorage so the
// state survives an in-tab reload but not a fresh session.
//
// The key is scoped per user id: logging out and back in as a different user in
// the same tab (sessionStorage survives the swap) must NOT leak one user's
// filters onto another's. Without the scope, a viewer would inherit whatever an
// admin last set. 'anon' is a defensive fallback for a missing id.
//
// …and per restaurant: switching restaurant remounts this page for the same
// user, so a user-only key carried one restaurant's period and supplier name
// into the next (field report, 03/10/2026). Each restaurant keeps its own
// filters; null restaurant = the "all restaurants" view.
const filtersKey = (uid, rid) => `peak.suppliersFilters.${uid ?? 'anon'}.${rid ?? 'all'}`;

// This screen is the לתשלום view: every supplier whose month is not fully paid
// — awaiting approval, needing review, approved, or mid-payment (שולם חלקית,
// money still outstanding). Fully-settled suppliers live on the שולם tab
// (/suppliers/paid). The rule is isOpenTabState / isPaidTabState in
// utils/paymentLifecycle.js, paired with the server lib, so the two tabs can
// never disagree with the control that selected them.

function readSavedFilters(uid, rid) {
  try { return JSON.parse(sessionStorage.getItem(filtersKey(uid, rid))) || {}; }
  catch { return {}; }
}

// ─── Component ────────────────────────────────────────────────────────────────
export default function SuppliersPage() {
  const { user, restaurantId, canEditPaymentTerms, canEdit, isAdmin } = useAuth();
  const { paymentTrackingEnabled } = useSettings();
  // 720 is where the filter system (and the view tabs with it) switches to mobile.
  // Gates the filter bar, the view tabs AND table-vs-cards — this page's whole
// mobile layout, not just its filter chrome. Renamed from isFilterMobile so
// nobody retunes it for filters alone and moves the table with it.
  const isMobileLayout   = useIsMobile();
  // Admins always edit suppliers via /manage-suppliers; this in-row PATCH
  // exists so viewers holding the per-user flag can edit payment_terms
  // without admin-level write access on the rest of the supplier record.
  const [paymentTermsModal, setPaymentTermsModal] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  // supplier_id → { note_count, total_amount, invoice_id, … }. Empty for
  // non-admins, who never fetch it (see loadUnlinked).
  const [unlinkedBySupplier, setUnlinkedBySupplier] = useState(new Map());
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', status: 'פעיל', hq_only: false, category_line_id: '' });
  const [openMenu,        setOpenMenu]        = useState(null);
  const [commentsModal,   setCommentsModal]   = useState(null);
  const [paymentModal,    setPaymentModal]    = useState(null);
  // Mobile supplier detail sheet — the supplier whose row was tapped (the
  // list row carries only the answer fields; everything else lives here).
  const [supplierSheet,   setSupplierSheet]   = useState(null);
  const [historyModal,    setHistoryModal]    = useState(null);
  // The supplier whose approve/cancel is in flight — the control disables its
  // action while set, so a double click cannot toggle twice.
  const [approvingId,     setApprovingId]     = useState(null);
  // The כרטסת whose scan is open (LedgerMark click) — a document id.
  const [ledgerDocId,     setLedgerDocId]     = useState(null);
  const [helpOpen, setHelpOpen] = useState(false);
  const [pageMenuOpen,    setPageMenuOpen]    = useState(false);
  const [reportsMenuOpen, setReportsMenuOpen] = useState(false);

  // Multi-supplier payment: a dedicated selection mode. While active, rows show
  // checkboxes and clicking a row toggles selection instead of navigating. The
  // Set holds the selected supplier ids. multiPayModal holds the suppliers to
  // confirm payment for. Set-based pattern mirrors LinkOpenCreditsModal.
  const [multiPayMode,        setMultiPayMode]        = useState(false);
  const [selectedSupplierIds, setSelectedSupplierIds] = useState(new Set());
  const [multiPayModal,       setMultiPayModal]       = useState(null);

  const toggleSupplier = (id) => {
    setSelectedSupplierIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const exitMultiPayMode = () => {
    setMultiPayMode(false);
    setSelectedSupplierIds(new Set());
  };

  // Shared between the desktop .action-row confirm button and the mobile
  // floating bar's confirm button — same action, different chrome per
  // breakpoint (see the action-row render below).
  const confirmMultiPay = () => setMultiPayModal(
    sortedSuppliers
      .filter(s => selectedSupplierIds.has(s.id))
      .map(s => ({ ...s, _range: effectiveDateRange(s) }))
  );

  // Restore any sticky filters saved in this browser session (see filtersKey).
  // Read once on mount, scoped to the current user and restaurant; falls back to
  // defaults when nothing is stored for them.
  const saved = useMemo(() => readSavedFilters(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  // A one-shot supplier name handed over from the היסטורית תשלומים tab's
  // "show in לתשלום" link wins over the sticky saved value on this mount only.
  // takeSupplierJump reads AND deletes the key, so it must run exactly once —
  // a ref guard (not useMemo/useState, which StrictMode double-invokes in dev
  // and would consume the jump twice) makes that safe.
  const jumpRef = useRef(null);
  if (jumpRef.current === null) jumpRef.current = takeSupplierJump(user?.id);
  const jumpName = jumpRef.current;
  const [nameFilter,    setNameFilter]    = useState(jumpName || saved.nameFilter || '');
  // Default the date filter to the current calendar month so the page lands
  // on "this month" data instead of every supplier's full history. Computed
  // once on mount so it stays stable across renders.
  const defaultRange = useMemo(() => currentMonthRange(), []);
  // The date range is SHARED with the Payments-tracking screen — prefer whatever
  // period was last viewed on either screen (falls back to this page's own saved
  // value, then the current month). Both are per restaurant (see filtersKey).
  // Both hold a preset as its key, re-evaluated to today (period.js,
  // toStoredPeriod) — never the dates it produced on the day it was picked.
  const sharedDate = useMemo(() => readSharedDateRange(user?.id, restaurantId), []); // eslint-disable-line react-hooks/exhaustive-deps
  const savedRange = useMemo(() => fromStoredPeriod(saved.period, PRESETS_MONTHLY), []); // eslint-disable-line react-hooks/exhaustive-deps
  const [dateFrom,      setDateFrom]      = useState(sharedDate?.from ?? savedRange?.from ?? defaultRange.from);
  const [dateTo,        setDateTo]        = useState(sharedDate?.to ?? savedRange?.to ?? defaultRange.to);
  // Rolls a preset forward when the tab comes back after the day/month changed,
  // and is the key the period is saved under (utils/usePresetPeriod.js).
  const presetKey = usePresetPeriod(dateFrom, dateTo, PRESETS_MONTHLY, (f, t) => { setDateFrom(f); setDateTo(t); });
  // Multi-select: empty array = "all terms" (no filter applied).
  const [termsFilter,    setTermsFilter]    = useState(saved.termsFilter ?? []);
  const [dueDateFilter,  setDueDateFilter]  = useState(saved.dueDateFilter ?? ''); // "overdue" | "soon" | "YYYY-MM-DD"
  // Multi-select on the lifecycle state (utils/paymentLifecycle.js). Stored
  // under its OWN key, not the old `docStatusFilter`: a session that still
  // holds ['תקין'] from before the lifecycle would otherwise filter this page
  // to nothing, with no chip explaining why.
  const [lifecycleFilter, setLifecycleFilter] = useState(saved.lifecycleFilter ?? []);

  const navigate = useNavigate();
  const location = useLocation();

  // Identifies the period on screen. Compared against loadedKey to tell
  // "these numbers describe what the header says" from "these numbers are the
  // previous period's, still on screen".
  const periodKey = `${dateFrom}|${dateTo}|${dueDateFilter || ''}`;

  // Token guard against out-of-order responses. The date picker's arrows fire
  // one load per click, and this query is heavy (per-supplier payable/paid
  // subqueries), so three quick clicks can leave three in flight — and whichever
  // lands LAST repaints the page, header saying יוני while the rows and the
  // metric strip are July's. It reads as "the month filter silently failed",
  // which is exactly how it was reported. Mirrors CreditsHistoryPage.
  const loadToken = useRef(0);
  // Which period the rows currently in state were fetched FOR. The strip is
  // gated on this rather than on `loading` alone because `loading` is set
  // inside the effect, which runs AFTER the click's state update has painted:
  // for one frame the header already says the new month while the old month's
  // rows are still what the strip is summing. One frame is enough to read a
  // number off a phone, and that is exactly how this was reported.
  const [loadedKey, setLoadedKey] = useState(null);
  const load = async () => {
    const token = ++loadToken.current;
    setLoading(true);
    setLoadError(false);
    try {
      const params = {};
      if (dateFrom)      params.date_from = dateFrom;
      if (dateTo)        params.date_to   = dateTo;
      if (dueDateFilter) params.due_date  = dueDateFilter;
      const res = await suppliersApi.getAll(params);
      if (token !== loadToken.current) return;   // superseded
      setSuppliers(res.data);
      setLoadedKey(periodKey);
      loadUnlinked();
    } catch {
      // A failed fetch must never fall through to the empty state — "no rows"
      // and "the request failed" are different facts (see InlineLoadError).
      if (token !== loadToken.current) return;
      setLoadError(true);
    } finally {
      if (token === loadToken.current) setLoading(false);
    }
  };

  // The הערות thread for a row: the supplier's own notes plus the notes on its
  // documents in the window the row is showing (same params the list used).
  const openComments = (s) => {
    const params = {};
    if (dueDateFilter) params.due_date = dueDateFilter;
    else {
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo)   params.date_to   = dateTo;
    }
    // The month a new note is ABOUT: the supplier's invoice month under the
    // due-date filter, else the range when it is one calendar month. A
    // multi-month view names none — the note then counts by its own date.
    const periodMonth = dueDateFilter
      ? requiredInvoiceMonth(s.payment_terms, dueDateFilter)
      : (dateFrom && dateTo && dateFrom.slice(0, 7) === dateTo.slice(0, 7) ? dateFrom.slice(0, 7) : null);
    setCommentsModal({
      referenceId: s.id,
      title: s.name,
      periodMonth,
      loadDocComments: () => suppliersApi.documentComments(s.id, params),
    });
  };

  // Suppliers with delivery notes that belong under a חשבונית מרכזת and are not
  // linked to it — their amount on this table is reading HIGH by the notes'
  // value (server/lib/unlinkedDeliveryNotes.js).
  //
  // A separate, deliberately unscoped-by-date fetch, and never allowed to fail
  // the page: this is a mark on a row, not the row's data. It is admin-only
  // (the server 403s everyone else) because only an admin can act on it —
  // PUT /documents/:id/links is requireAdmin, so marking a manager's row would
  // be pointing at work they are not allowed to do.
  //
  // Deliberately NOT a column on GET /suppliers: see the route comment in
  // server/routes/suppliers.js for the permission and cost reasoning.
  const loadUnlinked = async () => {
    if (!isAdmin) return;
    try {
      const res = await suppliersApi.unlinkedDeliveryNotes();
      setUnlinkedBySupplier(new Map((res.data || []).map(r => [r.supplier_id, r])));
    } catch {
      setUnlinkedBySupplier(new Map());
    }
  };

  // The mark itself. One definition for both breakpoints — the desktop cell and
  // the mobile meta line showed the delivery-notes-only asterisk with two
  // copy-pasted spans, and this is the second mark on the same row.
  const unlinkedMark = (s) => {
    const u = unlinkedBySupplier.get(s.id);
    if (!u) return null;
    return (
      <span className="flag-mark" tabIndex={0} onClick={e => e.stopPropagation()}>
        <Icon name="unlink" size={13} />
        <span className="dn-tip">
          {u.note_count === 1
            ? 'תעודת משלוח אחת לא מקושרת לחשבונית'
            : `${u.note_count} תעודות משלוח לא מקושרות לחשבונית`}
          {/* Only a חשבונית מרכזת can be linked to, so only then does opening
              the supplier lead to an action. */}
          {` (${formatCurrencyWhole(u.total_amount)}) — הסכום מוצג גבוה מדי.`}
          {u.linkable ? ' פתחי את הספק כדי לקשר.' : ' פתחי את הספק לפרטים.'}
        </span>
      </span>
    );
  };

  useEffect(() => { load(); }, [dateFrom, dateTo, dueDateFilter, location.key]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep the multi-pay selection honest: when the visible supplier set changes
  // (a filter narrowed it, or a refetch dropped a supplier), prune any selected
  // ids that are no longer shown. Otherwise the "נבחרו N" count and "select all"
  // state would reference suppliers the user can't see. A benign refetch that
  // returns the same ids is a no-op (we keep the same Set instance).
  useEffect(() => {
    setSelectedSupplierIds(prev => {
      if (prev.size === 0) return prev;
      const visible = new Set(suppliers.map(s => s.id));
      const kept = [...prev].filter(id => visible.has(id));
      return kept.length === prev.size ? prev : new Set(kept);
    });
  }, [suppliers]);

  // Re-fetch when the tab regains focus. The payment-status badge is computed
  // server-side from the payments table; without this listener, uploading a
  // doc in another tab (which auto-pays for הוראת קבע suppliers) leaves the
  // suppliers page showing stale "לא שולם" until something else triggers a
  // fetch. Also fires after the user hides+shows the tab.
  useEffect(() => {
    const onFocus = () => { if (!document.hidden) load(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onFocus);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onFocus);
    };
  }, [dateFrom, dateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // Persist the current filter set to sessionStorage on every change so it
  // survives navigation / reload within the open browser session. Cleared by
  // closing the tab or by clearFilters (which resets to defaults, re-persisted
  // here on the next render).
  useEffect(() => {
    try {
      sessionStorage.setItem(filtersKey(user?.id, restaurantId), JSON.stringify({
        nameFilter, period: toStoredPeriod(presetKey, dateFrom, dateTo),
        termsFilter, dueDateFilter, lifecycleFilter,
      }));
    } catch { /* storage unavailable (private mode quota) — sticky filters degrade gracefully */ }
  }, [user?.id, restaurantId, nameFilter, dateFrom, dateTo, presetKey, termsFilter, dueDateFilter, lifecycleFilter]);

  // Mirror the date range to the shared key so the Payments-tracking screen opens
  // on the same month the user was last viewing here (and vice-versa).
  useEffect(() => {
    if (user?.id) writeSharedDateRange(user.id, restaurantId, presetKey, dateFrom, dateTo);
  }, [user?.id, restaurantId, presetKey, dateFrom, dateTo]);

  // One per visit to this view, whether reached via the tabs or landed on
  // directly — the tab-click event alone misses bookmarks, refreshes and the
  // back button, which undercounts how much each view is really used.
  useEffect(() => {
    if (paymentTrackingEnabled) {
      track(EVENTS.PAYMENT_VIEW_SHOWN, { view: 'unpaid', entry: consumeViewEntry() });
    }
  }, [paymentTrackingEnabled]);

  // Spec §6: "Clear all" → page defaults (current-month range), not empty.
  const clearFilters = () => {
    const fresh = currentMonthRange();
    setNameFilter('');
    setDateFrom(fresh.from);
    setDateTo(fresh.to);
    setTermsFilter([]);
    setDueDateFilter('');
    // The paid/open split is not cleared here because it is not a filter — it
    // is the tab's fixed scope. Switching scope is done with the view tabs.
    setLifecycleFilter([]);
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    try {
      const r = await suppliersApi.create(form);
      // created:false = a supplier with this name (spaces and quote style
      // ignored) already exists for this restaurant; nothing was saved.
      // Refuse and keep the form open, so the name can be changed.
      if (r?.data?.created === false) {
        toast(`ספק בשם "${r.data.name}" כבר קיים — לא נשמר`, 'error');
        return;
      }
      toast('ספק נוסף בהצלחה');
      setShowModal(false);
      setForm({ name: '', status: 'פעיל', hq_only: false, category_line_id: '' });
      load();
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה בהוספת ספק', 'error');
    }
  };

  // The payment approval is stored per supplier per MONTH; a wider range is
  // approved month by month by the server (every month in it that holds a
  // document), so the control is available for any range.

  const handleApproval = async (supplierId) => {
    const row = suppliers.find(x => x.id === supplierId);
    if (approvingId != null) return;
    setApprovingId(supplierId);
    try {
      // Pass the whole range: the server approves (or withdraws) every month
      // in it that holds a document, so the toggle covers what the row shows.
      // The row's own window — under a due-date filter each supplier maps to
      // its invoice month (effectiveDateRange), exactly what its lifecycle was
      // computed over; approving the page range there acted on other months
      // and then swapped a wrong-month lifecycle into the row.
      const win = row ? effectiveDateRange(row) : { date_from: dateFrom, date_to: dateTo };
      const res = await suppliersApi.approve(supplierId, win.date_from, win.date_to);
      const { approved_by, approved_at, lifecycle } = res.data;
      track(EVENTS.SUPPLIER_MONTH_APPROVED, { now_approved: approved_by != null });
      // The server answers with the row's new lifecycle: swap it in, never
      // recompute it here, and never refetch the whole list for one toggle.
      const patch = (s) => ({ ...s, approved_by: approved_by ?? null, approved_at: approved_at ?? null, ...(lifecycle ? { lifecycle } : {}) });
      setSuppliers(prev => prev.map(s => (s.id === supplierId ? patch(s) : s)));
      setSupplierSheet(prev => (prev && prev.id === supplierId ? patch(prev) : prev));
    } catch (err) {
      toast(err?.response?.data?.error || 'שגיאה בעדכון אישור', 'error');
    } finally {
      setApprovingId(null);
    }
  };

  const hasDateFilter = dateFrom || dateTo;
  const termsOptions  = useMemo(
    () => [...new Set(suppliers.map(s => s.payment_terms).filter(Boolean))].sort(),
    [suppliers],
  );

  // Enrich each supplier with the computed due date. The payment lifecycle
  // itself arrives resolved from the server (`s.lifecycle`) — nothing about it
  // is derived here.
  const enriched = suppliers.map(s => ({
    ...s,
    _dueDate:      calcDueDate(s.payment_terms, s.last_doc_date),
    _dueFmt:       formatDueDate(calcDueDate(s.payment_terms, s.last_doc_date)),
    _dueStatus:    dueDateStatus(calcDueDate(s.payment_terms, s.last_doc_date)),
  }));

  // All filters EXCEPT the tab's paid/open scope. The "סה"כ רכש (כולל מע"מ)" purchasing
  // card sums over this set so it shows the FULL month's procurement (paid
  // included), while the table + its footer total apply the tab scope on top to
  // show only what's still owed.
  const filteredNoPayment = enriched.filter(s => {
    if (nameFilter && !s.name.includes(nameFilter)) return false;
    if (hasDateFilter && s.invoice_count === 0) return false;
    if (termsFilter.length > 0 && !termsFilter.includes(s.payment_terms)) return false;
    if (lifecycleFilter.length > 0 && !lifecycleFilter.includes(s.lifecycle?.state)) return false;
    return true;
  });
  const filtered = filteredNoPayment.filter(s => isOpenTabState(s.lifecycle?.state));

  // totalAmount = footer "סה"כ" AND the "סה"כ לתשלום" metric card = what is still
  // OWED across the rows on screen (sum of amount_unpaid). Both read the same
  // value, so the card can never disagree with the footer — which is what made a
  // gross total (incl. already-paid) look like a bug on partially-paid rows.
  const totalAmount = filtered.reduce((s, x) => s + (x.amount_unpaid ?? x.total_amount ?? 0), 0);
  // Which empty-state message to show when the לתשלום list is empty. All from
  // data already in hand: `filteredNoPayment` (name/date matches of ANY payment
  // status) minus `filtered` (open only) tells us "matches exist but they're all
  // paid" without a second call. The overflow filters (terms / doc-status / due)
  // fall back to the generic line since the tailored copy only reasons about
  // name + dates.
  const hasDateRange     = !!(dateFrom || dateTo);
  const overflowFilters  = termsFilter.length > 0 || lifecycleFilter.length > 0 || !!dueDateFilter;
  const emptyKind = (() => {
    if (overflowFilters) return 'fallback';
    if (nameFilter) {
      if (filteredNoPayment.length > 0) return 'all-paid';   // matches exist, all paid
      return hasDateRange ? 'no-match-range' : 'unknown';
    }
    if (filteredNoPayment.length > 0) return 'clean-period'; // docs in range, all paid
    return hasDateRange ? 'empty-range' : 'new-account';
  })();
  const jumpToPaid = () => {
    setSupplierJump(user?.id, nameFilter);
    navigate('/suppliers/paid');
  };
  const searchAllDates = () => { setDateFrom(''); setDateTo(''); };

  // Sortable columns on the suppliers table — name (Hebrew alpha), amount to pay
  // (numeric, the amount_unpaid the column now displays), due date (Date or
  // null → null lands last).
  const { sorted: sortedSuppliers, sort: supplierSort, setSortTo: setSupplierSort, indicator: sortInd, headerProps: sortableTh } = useSortable(filtered, {
    name:  s => s.name,
    total: s => (s.amount_unpaid ?? s.total_amount ?? 0),
    due:   s => s._dueDate,
  });

  // Tabs are hidden in multi-pay mode: navigating away mid-selection would
  // silently discard the selection.
  const showViewTabs = paymentTrackingEnabled && !multiPayMode;

  // Metric cards: 4 fixed cards, scoped to `filtered` — the rows actually on
  // screen — except "רכש", which sums `filteredNoPayment` (every payment status)
  // since total purchasing means the full period, not just what's still owed.
  // Order is ספקים → מסמכים → לתשלום → רכש on BOTH tabs.
  //
  // The "לתשלום" total is what's STILL owed (`totalAmount` = sum of
  // amount_unpaid), NOT the gross incl. already-paid — on the "to be paid" tab a
  // gross figure read as a bug, since it disagreed with the table footer for
  // partially-paid suppliers. It reconciles with the footer now. (amount_unpaid
  // comes from d.total, VAT-INCLUSIVE, hence מע"מ.)
  //
  // "רכש" reads `purchase_amount`, NOT `total_amount`: the two differ only on a
  // SETTLED credit, which total_amount stops subtracting (correct for "what do I
  // owe", wrong for "what did I buy" — the goods went back either way). See
  // PURCHASE_AMOUNT_CASE in server/lib/amountOwed.js.
  const grossTotal = filteredNoPayment.reduce((s, x) => s + (x.purchase_amount || 0), 0);
  // Owner indicator: the HQ-only part of the same רכש figure, same filters —
  // exactly what a manager's רכש card is lower by.
  const hiddenGross = filteredNoPayment.filter(s => s.hq_only).reduce((s, x) => s + (x.purchase_amount || 0), 0);
  const statCards = [
    { key: 'suppliers', value: filtered.length,                                 label: 'סה"כ ספקים' },
    { key: 'docs',      value: filtered.reduce((s, x) => s + (x.invoice_count || 0), 0), label: 'סה"כ מסמכים' },
    { key: 'total',     value: formatCurrencyWhole(totalAmount),                     label: 'סה"כ לתשלום (כולל מע"מ)' },
    { key: 'gross',     value: formatCurrencyWhole(grossTotal),                      label: 'סה"כ רכש (כולל מע"מ)',
      info: PURCHASE_INFO },
  ];

  // The date window that defines "this supplier's docs in the current view".
  // Under a due-date filter each supplier maps to its own invoice-month window
  // (via payment_terms); otherwise everyone shares the active date range. Used
  // for both row drill-down navigation and multi-pay, so the docs paid match
  // exactly the docs (and total) the user saw.
  const effectiveDateRange = (s) => {
    const ym = dueDateFilter ? requiredInvoiceMonth(s.payment_terms, dueDateFilter) : null;
    if (ym) {
      const [yy, mm] = ym.split('-');
      const lastDay = String(new Date(parseInt(yy), parseInt(mm), 0).getDate()).padStart(2, '0');
      return { date_from: `${ym}-01`, date_to: `${ym}-${lastDay}` };
    }
    return { date_from: dateFrom || undefined, date_to: dateTo || undefined };
  };
  // The "התקבלה כרטסת" mark — only when the latest ledger covers the row's
  // whole window (its end month); see utils/ledgerCoverage.js.
  const ledgerMarkFor = (s) => (
    ledgerCoversWindow(s.latest_ledger, effectiveDateRange(s).date_to)
      ? <LedgerMark ledger={s.latest_ledger} onOpen={setLedgerDocId} />
      : null
  );

  return (
    <div
      className="suppliers-page"
      // Reserves room for .mobile-bulk-bar (position: fixed) so it doesn't
      // cover the last supplier row / the sheet's own action buttons while
      // it's on screen. Only applies on the exact breakpoint+state combo
      // that renders the bar, so it never affects desktop.
      style={isMobileLayout && multiPayMode ? { paddingBottom: 64 } : undefined}
    >
      <div className="page-title-row">
        {/* Same title on both /suppliers and /suppliers/paid: they are two tabs of one
            screen, so the header must not change when you switch between them. */}
        <div className="page-title-group">
          <h1 className="page-title">ספקים - מעקב תשלומים</h1>
          <HqOnlyNotice money={formatCurrencyWhole} amount={loadError ? null : hiddenGross} pending={loading || loadedKey !== periodKey} basis="רכש בתקופה (כולל מע״מ)" />
        </div>
        <div className="page-title-actions">
          <div className="page-menu">
            <button className="btn btn-sm btn-excel"
              aria-expanded={reportsMenuOpen}
              onClick={() => setReportsMenuOpen(o => !o)}>
              <Icon name="spreadsheet" size={14} />
              דוחות
            </button>
            {reportsMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setReportsMenuOpen(false)} />
                <div className="page-menu-panel">
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      track(EVENTS.ANALYSIS_EXPORTED, { report_type: 'suppliers' });
                      // due_date must ride along with the range: the LIST call
                      // above sends it, and the server treats it as an
                      // OVERRIDE of the date window (it re-derives a per-supplier
                      // invoice month from payment terms). Omitting it here made
                      // the file cover a different set of documents than the
                      // table it was exported from.
                      suppliersApi.exportExcel({
                        ...(dateFrom      ? { date_from: dateFrom }      : {}),
                        ...(dateTo        ? { date_to:   dateTo }        : {}),
                        ...(dueDateFilter ? { due_date:  dueDateFilter } : {}),
                      });
                    }}
                    title="שורה לכל ספק עם סכום, שולם, יתרה לתשלום, מצב תשלום, תנאי ותאריך תשלום, אושר ע״י והערות — ובגיליון נוסף פירוט כל תשלום (אמצעי, הערות, נרשם ע״י)">
                    <Icon name="spreadsheet" size={15} /> סטטוס תשלומים לפי ספק
                  </button>
                  <button className="page-menu-item"
                    onClick={() => {
                      setReportsMenuOpen(false);
                      // due_date rides along here too — same reason as the
                      // report above. Without it this file listed documents
                      // from the plain date window while the table beside it
                      // was showing the due-date-derived months.
                      documentsApi.exportAllPayments({
                        ...(dateFrom      ? { date_from: dateFrom }      : {}),
                        ...(dateTo        ? { date_to:   dateTo }        : {}),
                        ...(dueDateFilter ? { due_date:  dueDateFilter } : {}),
                      });
                    }}
                    title="מספר מסמך, סכום לפני/כולל מע״מ, תנאי תשלום, תאריך לתשלום, סטטוס והערות — לכל מסמך">
                    <Icon name="spreadsheet" size={15} /> דוח תשלומים מפורט (לפי מסמך)
                  </button>
                </div>
              </>
            )}
          </div>
          <div className="page-menu">
            <button
              className="page-menu-trigger"
              aria-label="פעולות נוספות"
              aria-expanded={pageMenuOpen}
              onClick={() => setPageMenuOpen(o => !o)}
            >⋮</button>
            {pageMenuOpen && (
              <>
                <div className="menu-backdrop" onClick={() => setPageMenuOpen(false)} />
                <div className="page-menu-panel">
                  {isAdmin && (
                    <button className="page-menu-item"
                      onClick={() => { setPageMenuOpen(false); setShowModal(true); }}>
                      + הוסף ספק
                    </button>
                  )}
                  <button className="page-menu-item" disabled={loading}
                    onClick={() => { setPageMenuOpen(false); load(); }}
                    title="טען מחדש מהמסד נתונים">
                    <Icon name="recurring" size={15} /> רענן
                  </button>
                  <button className="page-menu-item"
                    onClick={() => { setPageMenuOpen(false); setHelpOpen(true); }}>
                    <Icon name="info" size={15} /> חשוב לדעת
                  </button>
                  <ViewAsMenuItems onDone={() => setPageMenuOpen(false)} />
                </div>
              </>
            )}
          </div>
          {helpOpen && <PaymentTrackingHelp tab="open" onClose={() => setHelpOpen(false)} />}
        </div>
      </div>

      {/* Tabs dock to the list card at every breakpoint now — see the .card
          below. They used to ride above this filter bar on mobile only
          ("the tab decides which filters exist below it," since /suppliers
          and /suppliers/paid mount different filter-bar components), but
          that gave mobile and desktop two different mental models for where
          the tabs live relative to the same content. One placement, always
          matching desktop's "folder tabs on the card." */}
      <SuppliersFilterBar
        nameFilter={nameFilter}             setNameFilter={setNameFilter}
        termsFilter={termsFilter}           setTermsFilter={setTermsFilter}
        termsOptions={termsOptions}
        dueDateFilter={dueDateFilter}       setDueDateFilter={setDueDateFilter}
        lifecycleFilter={lifecycleFilter}   setLifecycleFilter={setLifecycleFilter}
        dateFrom={dateFrom}                 dateTo={dateTo}
        setDateRange={(f, t) => { setDateFrom(f); setDateTo(t); }}
        onClearAll={clearFilters}
        resultCount={sortedSuppliers.length}
        sortOptions={[
          { key: 'due',   label: 'תאריך לתשלום' },
          { key: 'total', label: 'סכום לתשלום' },
          { key: 'name',  label: 'שם ספק' },
        ]}
        sort={supplierSort}
        onSortChange={setSupplierSort}
      />

      {/* Strip is hidden when there are no rows: metrics of a zero-result view
          (e.g. searching a fully-paid supplier) would read "0 / 0 / ₪0" over
          an empty-state message that already explains the situation.
          Hidden on a load FAILURE for the same reason: `rows` keeps the previous
          period's data so a retry can restore it without a flash, which means an
          unguarded strip would leave four confident numbers standing over "לא
          ניתן לטעון". Guarded here rather than by clearing rows in the catch —
          the empty-state copy reads `rows` too, and emptying it would silently
          change which message the user gets. */}
      {!loadError && filtered.length > 0 && (
        <StatCardsRow cards={statCards} loading={loading || loadedKey !== periodKey} />
      )}

      <div className="card">
        {/* The card's own header row, on every breakpoint — a tab sitting on
            top of a borderless, shadowed card can't be made to look joined
            to it, so it renders INSIDE. CSS (.suppliers-page .view-tabs)
            reshapes it to a slim underline on mobile; the component itself
            doesn't know or care which breakpoint it's on. */}
        {showViewTabs && <PaymentViewTabs active="unpaid" />}

        {/* Mobile gets different CHROME for the same state, not different
            state: a full-width row costs a whole line of above-the-fold
            height on a phone, so the trigger shrinks to an icon and the
            confirm/cancel pair moves to a fixed bottom bar once something is
            selected — mirroring how the desktop version already swaps the
            trigger button for the counter/confirm/cancel cluster on the same
            multiPayMode flip. See .mobile-bulk-bar in index.css — the first
            sticky-bottom-bar pattern in this codebase, kept scoped to
            .suppliers-page on purpose. */}
        {paymentTrackingEnabled && canEdit && (
          multiPayMode ? (
            isMobileLayout ? (
              <div className="mobile-bulk-bar">
                <span className="mobile-bulk-bar-count">נבחרו {selectedSupplierIds.size} ספקים</span>
                <button className="btn btn-sm btn-secondary" onClick={exitMultiPayMode}>
                  ביטול
                </button>
                <button className="btn btn-sm btn-success"
                  disabled={selectedSupplierIds.size === 0}
                  onClick={confirmMultiPay}>
                  <Icon name="card" size={15} /> בוצע תשלום
                </button>
              </div>
            ) : (
              <div className="action-row">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <span style={{ fontWeight: 600, color: 'var(--ink)' }}>
                    נבחרו {selectedSupplierIds.size} ספקים
                  </span>
                  <button className="btn btn-sm btn-success"
                    disabled={selectedSupplierIds.size === 0}
                    onClick={confirmMultiPay}>
                    <Icon name="card" size={15} /> בוצע תשלום
                  </button>
                  <button className="btn btn-sm btn-secondary" onClick={exitMultiPayMode}>
                    ביטול
                  </button>
                </div>
              </div>
            )
          ) : (
            <div className={`action-row${isMobileLayout ? ' action-row--mobile-compact' : ''}`}>
              <button
                className={`btn btn-sm btn-secondary${isMobileLayout ? ' icon-only-btn' : ''}`}
                onClick={() => setMultiPayMode(true)}
                title="סמני כמה ספקים ותעדי עליהם תשלום יחד"
                aria-label={isMobileLayout ? 'תשלום מרובה' : undefined}>
                <Icon name="card" size={isMobileLayout ? 16 : 15} />
                {!isMobileLayout && ' תשלום מרובה'}
              </button>
            </div>
          )
        )}

        {loading ? (
          <div className="loading-container"><div className="spinner" /><span>טוען...</span></div>
        ) : loadError ? (
          <InlineLoadError message="לא ניתן לטעון את הספקים" onRetry={load} />
        ) : filtered.length === 0 ? (
          <PaymentEmptyState
            period={dateFrom || dateTo ? periodLabel(dateFrom, dateTo) : ''}
            tab="unpaid"
            kind={emptyKind}
            supplierName={nameFilter}
            onJump={jumpToPaid}
            onSearchAllDates={searchAllDates}
          />
        ) : (
          <>
          {!isMobileLayout && (
          <div className="table-wrapper">
            <table>
              <thead>
                <tr>
                  {multiPayMode && (
                    <th style={{ width: 40, textAlign: 'center' }}>
                      <input
                        type="checkbox"
                        checked={sortedSuppliers.length > 0 && selectedSupplierIds.size === sortedSuppliers.length}
                        onChange={() => setSelectedSupplierIds(prev =>
                          prev.size === sortedSuppliers.length ? new Set() : new Set(sortedSuppliers.map(s => s.id))
                        )}
                        title="בחר הכל"
                      />
                    </th>
                  )}
                  <th {...sortableTh('name')}>שם ספק{sortInd('name')}</th>
                  <th {...sortableTh('total')}>לתשלום{sortInd('total')}</th>
                  {/* One column for where this supplier-month is in the payment
                      process (utils/paymentLifecycle.js) — it replaced a
                      document-status roll-up whose "תקין" read as "checked, pay
                      it", plus a separate נבדק circle and a coloured payment
                      label under the due date. Click it to approve. */}
                  <th style={{ minWidth: 150 }}>מצב תשלום<LifecycleHelp /></th>
                  <th>תנאי תשלום</th>
                  <th {...sortableTh('due')}>תאריך לתשלום{sortInd('due')}</th>
                  <th style={{ width: 70 }}>הערות</th>
                  <th style={{ width: 36 }}></th>
                </tr>
              </thead>
              <tbody>
                {sortedSuppliers.map(s => {
                  const st      = s._dueStatus;
                  // Once any payment landed (full or partial) the urgency tint
                  // is misleading — the user has already acted on the bill.
                  const isPaid  = isPaidTabState(s.lifecycle?.state);
                  const rowBg   = isPaid ? undefined
                                : st === 'overdue' ? 'var(--danger-bg)'
                                : st === 'soon'    ? 'var(--warning-bg)'
                                : undefined;
                  const menuBtn = { display: 'block', width: '100%', padding: '9px 16px', background: 'none', border: 'none', textAlign: 'right', cursor: 'pointer', fontSize: '0.88rem', color: 'var(--ink)', fontFamily: 'inherit' };
                  const mIcon   = paymentMethodIcon(s.payment_method);
                  return (
                    <tr key={s.id} className="row-clickable"
                      onClick={() => {
                        // In multi-pay selection mode a row click toggles
                        // selection instead of navigating into the supplier.
                        if (multiPayMode) { toggleSupplier(s.id); return; }
                        const qp = new URLSearchParams();
                        // Drill into the same date window the row represents (see
                        // effectiveDateRange). Under a due-date filter also pass
                        // due_date for the banner display.
                        const { date_from, date_to } = effectiveDateRange(s);
                        if (date_from) qp.set('date_from', date_from);
                        if (date_to)   qp.set('date_to',   date_to);
                        if (dueDateFilter && requiredInvoiceMonth(s.payment_terms, dueDateFilter)) {
                          qp.set('due_date', dueDateFilter);
                        }
                        const qs = qp.toString();
                        navigate(`/supplier/${s.id}${qs ? '?' + qs : ''}`);
                      }}
                      style={{ background: selectedSupplierIds.has(s.id) ? 'var(--selected-soft-bg)' : rowBg }}>
                      {multiPayMode && (
                        <td style={{ width: 40, textAlign: 'center' }}>
                          <input type="checkbox" checked={selectedSupplierIds.has(s.id)}
                            onChange={() => toggleSupplier(s.id)} onClick={e => e.stopPropagation()} />
                        </td>
                      )}
                      {/* The document count rides under the name as secondary
                          text (exactly as the mobile list shows it) — a full
                          column for one small number starved מצב תשלום, whose
                          badge was wrapping onto two lines on a laptop. */}
                      <td>
                        <div style={{ fontWeight: 600 }}>{s.name}<HqOnlyLock hqOnly={s.hq_only} />{ledgerMarkFor(s)}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--ink-muted)', marginTop: 2 }}>
                          {s.invoice_count === 1 ? 'מסמך אחד' : `${s.invoice_count} מסמכים`}
                        </div>
                      </td>
                      <td>
                        {(() => {
                          // Show the amount STILL OWED as the actionable figure.
                          // For a partially-paid row, the secondary line states the
                          // whole relationship in words — "שולם {paid} מתוך {total}"
                          // ("paid X out of Y") — so the hero is unambiguously the
                          // remainder, not a gross bill to subtract from. Without the
                          // total anchor the hero read either way (a real reported bug).
                          const unpaid = s.amount_unpaid ?? s.total_amount ?? 0;
                          const paid   = s.amount_paid ?? 0;
                          const gross  = supplierGross(s);
                          return (
                            <div style={{ lineHeight: 1.3 }}>
                              <span
                                onClick={e => {
                                  e.stopPropagation();
                                  copyToClipboard(String(roundWhole(unpaid)))
                                    .then(() => toast('הסכום הועתק'))
                                    .catch(() => toast('שגיאה בהעתקה', 'error'));
                                }}
                                title="לחצי להעתקת הסכום לתשלום"
                                style={{ cursor: 'pointer', borderBottom: '1px dashed var(--ink-faint)', fontWeight: 600 }}
                              >{formatCurrencyWhole(unpaid)}</span>
                              {s.delivery_notes_only ? (
                                <span className="dn-star" tabIndex={0}>*
                                  <span className="dn-tip">הסכום מבוסס על תעודות משלוח בלבד — טרם התקבלה חשבונית לחודש זה</span>
                                </span>
                              ) : null}
                              {/* Second mark on the same figure, and a
                                  different claim: the asterisk says the amount
                                  is provisional, this says it is double-counted.
                                  Admin-only; renders nothing otherwise. */}
                              {unlinkedMark(s)}
                              {/* Partially-paid: "paid X out of Y". Green = money
                                  that already went out (a settled fact, not an
                                  alert); the grey "מתוך {total}" is the anchor that
                                  makes the hero read as the remainder. */}
                              {paid > 0 ? (
                                <div style={{ fontSize: '0.72rem', marginTop: 2, whiteSpace: 'nowrap' }}>
                                  <span style={{ color: 'var(--success-ink)', fontWeight: 600 }}>שולם {formatCurrencyWhole(paid)}</span>
                                  <span style={{ color: 'var(--ink-muted)' }}> מתוך {formatCurrencyWhole(gross)}</span>
                                </div>
                              ) : null}
                            </div>
                          );
                        })()}
                      </td>
                      <td onClick={e => e.stopPropagation()}>
                        <PaymentLifecycleControl
                          lifecycle={s.lifecycle}
                          canApprove={canEdit}
                          busy={approvingId === s.id}
                          onToggle={() => handleApproval(s.id)}
                        />
                      </td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>{s.payment_terms || '—'}</td>
                      <td style={{ fontSize: '0.85rem', color: 'var(--ink-muted)' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                          {/* Always reserve the icon slot so the column lines
                             up across rows; render the icon when the supplier
                             has a known method, an empty placeholder otherwise. */}
                          {/* role="img" is load-bearing: the glyph inside is
                              aria-hidden, and an aria-label on a bare span (role
                              generic) is not announced — so without it the method
                              reaches screen readers nowhere. */}
                          <span
                            role="img"
                            title={s.payment_method || ''}
                            aria-label={s.payment_method || 'אין אמצעי תשלום'}
                            style={{ flexShrink: 0, lineHeight: 1, cursor: s.payment_method ? 'help' : 'default', minWidth: 18, display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
                          >
                            {mIcon ? <Icon name={mIcon} size={15} /> : null}
                          </span>
                          <div style={{ flex: 1 }}>
                            {s._dueDate ? (
                              <div style={{ color: isPaid ? 'var(--ink-muted)' : st === 'overdue' ? 'var(--danger-ink)' : st === 'soon' ? 'var(--warning-ink)' : 'var(--ink-muted)' }}>
                                {dueDateFilter ? fmtIsoToDisplay(dueDateFilter) : s._dueFmt}
                              </div>
                            ) : (
                              // No terms ⇒ no due date. The תנאי תשלום cell
                              // already says "—" for this row; a second dash
                              // here read as a second missing datum.
                              <span style={{ color: 'var(--ink-subtle)' }}>{s.payment_terms ? '—' : ''}</span>
                            )}
                          </div>
                        </div>
                      </td>

                      {/* הערות — the same cell the supplier page has: a control
                          whether or not there are notes (blank = הוסף הערה).
                          It lights up on supplier notes written INSIDE the
                          selected period, plus notes on the row's own
                          documents — those belong to the DOCUMENT's month, so
                          a September invoice commented on in October still
                          lights September. The click opens both. */}
                      <td onClick={e => e.stopPropagation()}>
                        {(() => {
                          const n = (s.comment_count_in_range || 0) + (s.doc_comment_count_in_range || 0);
                          const all = s.comment_count || 0;
                          const tip = n > 0 ? `${n} הערות` : all > 0 ? `${all} הערות ישנות יותר` : 'הוסף הערה';
                          return (
                            <button
                              type="button"
                              className={`note-cell${n > 0 ? ' is-active' : ''}`}
                              onClick={() => openComments(s)}
                              data-tip={tip}
                              aria-label={n > 0 ? `הערות: ${n}` : tip}
                            >
                              <Icon name="comment" size={14} />
                              {n > 0 && <span>{n}</span>}
                            </button>
                          );
                        })()}
                      </td>
                      {/* ⋮ menu */}
                      <td onClick={e => e.stopPropagation()} style={{ position: 'relative', width: 36, textAlign: 'center' }}>
                        <button
                          className="row-menu-trigger" style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.1rem', color: 'var(--ink-subtle)', padding: '2px 4px', position: 'relative', lineHeight: 1 }}
                          onClick={() => setOpenMenu(openMenu === s.id ? null : s.id)}
                        >
                          ⋮
                        </button>
                        {openMenu === s.id && (
                          <>
                            <div style={{ position: 'fixed', inset: 0, zIndex: 198 }} onClick={() => setOpenMenu(null)} />
                            <div style={{ position: 'absolute', left: 0, top: '100%', zIndex: 199, background: '#fff', border: '1px solid var(--line)', borderRadius: 10, boxShadow: '0 4px 20px rgba(0,0,0,0.15)', minWidth: 190, overflow: 'hidden' }}>
                              {/* הוסף הערה / צפה בהערות live in the הערות cell now
                                  (same as the supplier page) — two menu items that
                                  ran one handler. */}
                              {canEditPaymentTerms && (
                                <>
                                  <div style={{ borderTop: '1px solid var(--hairline)' }} />
                                  <button className="row-menu-item" style={menuBtn}
                                    onClick={() => { setOpenMenu(null); setPaymentTermsModal({ supplierId: s.id, supplierName: s.name, payment_terms: s.payment_terms || '', payment_method: s.payment_method || '' }); }}>
                                    <Icon name="edit" size={16} /> ערוך תנאי תשלום
                                  </button>
                                </>
                              )}
                              {paymentTrackingEnabled && canEdit && (
                                <>
                                  <div style={{ borderTop: '1px solid var(--hairline)' }} />
                                  <button className="row-menu-item" style={{ ...menuBtn, color: 'var(--success-ink)', fontWeight: 600 }}
                                    onClick={() => { setOpenMenu(null); const r = effectiveDateRange(s); setPaymentModal({ supplierId: s.id, supplierName: s.name, dateFrom: r.date_from, dateTo: r.date_to, lifecycle: s.lifecycle }); }}>
                                    <Icon name="card" size={16} /> בוצע תשלום
                                  </button>
                                  <button className="row-menu-item" style={menuBtn}
                                    onClick={() => { setOpenMenu(null); setHistoryModal({ supplierId: s.id, supplierName: s.name }); }}>
                                    <Icon name="history" size={16} /> היסטוריית תשלומים
                                  </button>
                                </>
                              )}
                            </div>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr style={{ background: 'var(--tint-strong)', fontWeight: 700, borderTop: '2px solid var(--outline)' }}>
                  <td style={{ padding: '10px 16px', color: 'var(--ink)' }}>
                    סה"כ
                    <span style={{ fontWeight: 400, fontSize: '0.75rem', color: 'var(--ink-muted)', marginInlineStart: 8 }}>
                      {filtered.reduce((s, x) => s + (x.invoice_count || 0), 0)} מסמכים
                    </span>
                  </td>
                  <td style={{ padding: '10px 16px' }}>{formatCurrencyWhole(totalAmount)}</td>
                  <td></td><td></td><td></td><td></td><td></td>
                </tr>
              </tfoot>
            </table>
            {sortedSuppliers.some(s => s.delivery_notes_only) && (
              <p style={{ fontSize: 12, color: 'var(--warning-ink)', margin: '6px 16px' }}>
                * הסכום מבוסס על תעודות משלוח בלבד — טרם התקבלה חשבונית לחודש הנבחר
              </p>
            )}
            {/* Legend for the second mark, same shape as the asterisk's. A
                glyph nobody can name is decoration; the tooltip explains one
                row, this explains the column. */}
            {sortedSuppliers.some(s => unlinkedBySupplier.has(s.id)) && (
              <p style={{ fontSize: 12, color: 'var(--warning-ink)', margin: '6px 16px', display: 'flex', alignItems: 'center', gap: 5 }}>
                <Icon name="unlink" size={13} />
                תעודות משלוח שטרם קושרו לחשבונית מרכזת — הסכום נספר פעמיים עד שיקושרו
              </p>
            )}
          </div>
          )}

          {/* Mobile (≤640px): two-line list rows (shared ListRow anatomy) —
              same sortedSuppliers, so filters apply identically. Row shows
              only the answer (who / how much / when); tapping opens a detail
              sheet with the rest + all actions. In multi-pay mode a tap
              toggles selection instead. */}
          {isMobileLayout && (
          <div>
            <div className="m-list">
            {sortedSuppliers.map(s => {
              const st     = s._dueStatus;
              const isPaid = isPaidTabState(s.lifecycle?.state);
              const unpaid = s.amount_unpaid ?? s.total_amount ?? 0;
              const rowCls = selectedSupplierIds.has(s.id) ? 'is-selected'
                           : isPaid ? ''
                           : st === 'overdue' ? 'is-alert'
                           : st === 'soon'    ? 'is-warn'
                           : '';
              const dueTxt = s._dueDate ? (dueDateFilter ? fmtIsoToDisplay(dueDateFilter) : s._dueFmt) : null;
              // Partially paid shows "שולם {paid} מתוך {total}" (green + grey), the
              // same wording as desktop so both breakpoints tell one story. Folded
              // into the meta line rather than a separate endSub line under the
              // amount — a long name plus a partial payment used to grow a row to
              // three-plus visual lines. The state itself is the row's badge.
              const statusNode = s.amount_paid > 0
                ? <span style={{ whiteSpace: 'nowrap' }}>
                    · <span style={{ color: 'var(--success-ink)', fontWeight: 600 }}>שולם {formatCurrencyWhole(s.amount_paid)}</span>
                    <span style={{ color: 'var(--ink-muted)' }}> מתוך {formatCurrencyWhole(supplierGross(s))}</span>
                  </span>
                : null;
              return (
                <ListRow
                  key={s.id}
                  className={rowCls}
                  primary={<>{s.name}<HqOnlyLock hqOnly={s.hq_only} /></>}
                  meta={
                    <>
                      <span>{s.invoice_count} מסמכים</span>
                      {statusNode}
                      {dueTxt && (
                        <span style={{ color: isPaid ? undefined : st === 'overdue' ? 'var(--danger-ink)' : st === 'soon' ? 'var(--warning-ink)' : undefined }}>
                          · לתשלום {dueTxt}
                        </span>
                      )}
                      {/* delivery_notes_only is a SQLite 0/1 int — a bare &&
                          would render the 0 into the row. */}
                      {s.delivery_notes_only ? (
                        <span className="dn-star" tabIndex={0} onClick={e => e.stopPropagation()}>*
                          <span className="dn-tip">הסכום מבוסס על תעודות משלוח בלבד — טרם התקבלה חשבונית לחודש זה</span>
                        </span>
                      ) : null}
                      {unlinkedMark(s)}
                    </>
                  }
                  // The lifecycle state is the row's badge on every row: unlike
                  // the old doc-status roll-up (where "תקין" on every row was
                  // noise), each state here is an answer — waiting on whom, or
                  // paid how far. Approving happens from the detail sheet.
                  // The ledger mark ends the meta line, not the name line: the
                  // name clamps with overflow: hidden, which would clip its
                  // 44px tap box. Last, so the box extends into empty space.
                  badge={<><PaymentLifecycleBadge lifecycle={s.lifecycle} />{ledgerMarkFor(s)}</>}
                  amount={formatCurrencyWhole(unpaid)}
                  onClick={multiPayMode ? () => toggleSupplier(s.id) : () => setSupplierSheet(s)}
                />
              );
            })}
            </div>
          </div>
          )}
          </>
        )}
      </div>

      {/* Mobile supplier detail sheet — the fields the compact row drops
          plus every action. Each action closes the sheet first so its
          modal doesn't stack under the portal. */}
      {supplierSheet && (() => {
        const s      = supplierSheet;
        // Approving a month that already has money against it is moot — the
        // money fact wins in the lifecycle — so the sheet hides the toggle.
        const isPaid = (row) => isPaidTabState(row.lifecycle?.state);
        const unpaid = s.amount_unpaid ?? s.total_amount ?? 0;
        const paid   = s.amount_paid ?? 0;
        const mIcon  = paymentMethodIcon(s.payment_method);
        const openDocs = () => {
          const qp = new URLSearchParams();
          const { date_from, date_to } = effectiveDateRange(s);
          if (date_from) qp.set('date_from', date_from);
          if (date_to)   qp.set('date_to',   date_to);
          if (dueDateFilter && requiredInvoiceMonth(s.payment_terms, dueDateFilter)) {
            qp.set('due_date', dueDateFilter);
          }
          const qs = qp.toString();
          setSupplierSheet(null);
          navigate(`/supplier/${s.id}${qs ? '?' + qs : ''}`);
        };
        // SVG, not a text '‹': that character is bidi-mirrored and RTL
        // rendering flips it backward. The SVG always points left (forward).
        const navChevron = (
          <svg className="sheet-nav-chevron" width="15" height="15" viewBox="0 0 24 24"
               aria-hidden="true" fill="none" stroke="currentColor" strokeWidth="2"
               strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
        );
        return (
          <FilterSheet open onClose={() => setSupplierSheet(null)} title={s.name}>
            {/* Answer-first: the open balance is THE question this sheet
                answers, so it gets hero treatment; context is a one-line
                sub; details a quiet kv card; one primary action; the rest
                are settings-style navigation rows. */}
            <div className="sheet-hero">
              <div className="sheet-hero-label">לתשלום</div>
              <div className="sheet-hero-row">
                <span className="sheet-hero-amount">{formatCurrencyWhole(unpaid)}</span>
                <button
                  type="button"
                  className="sheet-copy-btn"
                  aria-label="העתק את הסכום לתשלום"
                  title="העתקת הסכום לתשלום"
                  onClick={() => {
                    copyToClipboard(String(roundWhole(unpaid)))
                      .then(() => toast('הסכום הועתק'))
                      .catch(() => toast('שגיאה בהעתקה', 'error'));
                  }}
                >
                  <svg width="15" height="15" viewBox="0 0 24 24" aria-hidden="true"
                       fill="none" stroke="currentColor" strokeWidth="2"
                       strokeLinecap="round" strokeLinejoin="round">
                    <rect x="9" y="9" width="12" height="12" rx="2" />
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                  </svg>
                </button>
              </div>
              <div className="sheet-hero-sub">
                {paid > 0 && <span style={{ color: 'var(--success-ink)' }}>שולם {formatCurrencyWhole(paid)} מתוך {formatCurrencyWhole(supplierGross(s))}</span>}
                {paid > 0 && <span>·</span>}
                <span>{s.invoice_count} מסמכים</span>
                <PaymentLifecycleBadge lifecycle={s.lifecycle} />
              </div>
            </div>

            <div>
              <div className="sheet-kv">
                <label>תנאי תשלום</label>
                <span>{s.payment_terms || '—'}</span>
              </div>
              <div className="sheet-kv">
                <label>תאריך לתשלום</label>
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                  {/* Same labelling as the desktop column — the glyph is
                      aria-hidden, so the method needs a name of its own. */}
                  {mIcon && (
                    <span role="img" title={s.payment_method} aria-label={s.payment_method} style={{ display: 'inline-flex' }}>
                      <Icon name={mIcon} size={15} />
                    </span>
                  )}
                  {s._dueDate ? (dueDateFilter ? fmtIsoToDisplay(dueDateFilter) : s._dueFmt) : '—'}
                </span>
              </div>
              {s.lifecycle?.approved_by && (
                <div className="sheet-kv">
                  <label>אושר ע״י</label>
                  <span style={{ color: 'var(--success-ink)', display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                    <Icon name="check-circle" size={15} /> {s.lifecycle.approved_by}
                  </span>
                </div>
              )}
            </div>

            {paymentTrackingEnabled && canEdit && (
              <div className="sheet-actions">
                <button className="btn btn-success"
                  onClick={() => {
                    const r = effectiveDateRange(s);
                    setSupplierSheet(null);
                    setPaymentModal({ supplierId: s.id, supplierName: s.name, dateFrom: r.date_from, dateTo: r.date_to, lifecycle: s.lifecycle });
                  }}>
                  <Icon name="card" size={16} /> תשלום
                </button>
                {/* The approve action for the phone: the row badge is display-
                    only there, so the sheet carries the toggle. Disabled (with
                    the reason) when the range names no single month. */}
                {!isPaid(s) && (
                  <button className="btn btn-secondary"
                    disabled={approvingId === s.id}
                    onClick={() => handleApproval(s.id)}>
                    <Icon name={s.lifecycle?.approved_by ? 'x-circle' : 'check-circle'} size={16} />
                    {s.lifecycle?.approved_by ? ' בטל אישור' : ' אשר לתשלום'}
                  </button>
                )}
              </div>
            )}

            <div className="sheet-nav">
              <button type="button" className="sheet-nav-row" onClick={openDocs}>
                <Icon name="documents" size={16} /> מסמכי הספק
                {navChevron}
              </button>
              {canEditPaymentTerms && (
                <button type="button" className="sheet-nav-row"
                  onClick={() => {
                    setSupplierSheet(null);
                    setPaymentTermsModal({ supplierId: s.id, supplierName: s.name, payment_terms: s.payment_terms || '', payment_method: s.payment_method || '' });
                  }}>
                  <Icon name="edit" size={16} /> תנאי תשלום
                  {navChevron}
                </button>
              )}
              <button type="button" className="sheet-nav-row"
                onClick={() => {
                  setSupplierSheet(null);
                  openComments(s);
                }}>
                <Icon name="comment" size={16} /> הערות{(s.comment_count || 0) + (s.doc_comment_count_in_range || 0) > 0 ? ` (${(s.comment_count || 0) + (s.doc_comment_count_in_range || 0)})` : ''}
                {navChevron}
              </button>
            </div>
          </FilterSheet>
        );
      })()}

      {showModal && (
        <Modal
          onClose={() => setShowModal(false)}
          closeOnBackdrop={false}
          ariaLabel="הוספת ספק חדש"
        >
          <h2 className="modal-title">הוספת ספק חדש</h2>
          <form onSubmit={handleAdd}>
            <div className="form-group">
              <label>שם ספק *</label>
              <input className="form-control" value={form.name}
                onChange={e => setForm({ ...form, name: e.target.value })} required placeholder="שם הספק" />
            </div>
            <div className="form-group">
              <label>סטטוס</label>
              <select className="form-control" value={form.status}
                onChange={e => setForm({ ...form, status: e.target.value })}>
                <option>פעיל</option>
                <option>מושהה</option>
                <option>לא פעיל</option>
              </select>
            </div>
            <NewSupplierOptions
              name={form.name}
              hqOnly={form.hq_only}
              onHqOnlyChange={hq_only => setForm(current => ({ ...current, hq_only }))}
              categoryLineId={form.category_line_id}
              onCategoryLineChange={category_line_id => setForm(current => ({ ...current, category_line_id }))}
            />
            <div className="modal-actions">
              <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>ביטול</button>
              <button type="submit" className="btn btn-primary">שמור</button>
            </div>
          </form>
        </Modal>
      )}

      {commentsModal && (
        <CommentsModal
          money={formatCurrencyWhole}
          type="supplier"
          referenceId={commentsModal.referenceId}
          title={commentsModal.title}
          loadDocComments={commentsModal.loadDocComments}
          periodMonth={commentsModal.periodMonth}
          onClose={() => setCommentsModal(null)}
          onCommentAdded={() => setSuppliers(prev => prev.map(s =>
            s.id === commentsModal.referenceId ? { ...s, comment_count: (s.comment_count || 0) + 1, comment_count_in_range: (s.comment_count_in_range || 0) + 1 } : s
          ))}
        />
      )}

      {ledgerDocId != null && (
        <LedgerViewer docId={ledgerDocId} onClose={() => setLedgerDocId(null)} />
      )}

      {paymentModal && (
        <PaymentModal
          supplierId={paymentModal.supplierId}
          supplierName={paymentModal.supplierName}
          dateFrom={paymentModal.dateFrom}
          dateTo={paymentModal.dateTo}
          approvalOffer={approvalOfferForRange({ lifecycle: paymentModal.lifecycle, dateFrom: paymentModal.dateFrom, dateTo: paymentModal.dateTo })}
          onSuccess={() => { setPaymentModal(null); load(); }}
          onClose={() => setPaymentModal(null)}
        />
      )}

      {multiPayModal && (
        <MultiPaymentModal
          suppliers={multiPayModal}
          dateFrom={dateFrom}
          dateTo={dateTo}
          onSuccess={() => { setMultiPayModal(null); exitMultiPayMode(); load(); }}
          onClose={() => setMultiPayModal(null)}
        />
      )}

      {historyModal && (
        <PaymentHistoryModal
          supplierId={historyModal.supplierId}
          supplierName={historyModal.supplierName}
          canDelete={isAdmin}
          onChange={load}
          onClose={() => setHistoryModal(null)}
        />
      )}
      {paymentTermsModal && (
        <PaymentTermsModal
          supplier={paymentTermsModal}
          onClose={() => setPaymentTermsModal(null)}
          onSaved={(updated) => {
            setSuppliers(prev => prev.map(s =>
              s.id === paymentTermsModal.supplierId
                ? { ...s, payment_terms: updated.payment_terms, payment_method: updated.payment_method }
                : s
            ));
            setPaymentTermsModal(null);
          }}
        />
      )}
    </div>
  );
}

// Tiny edit modal used by viewers with can_edit_payment_terms and (optionally)
// admins. Talks to the dedicated PATCH endpoint so only these two fields can
// be touched — accidental edits to other supplier fields are impossible from
// this surface.
function PaymentTermsModal({ supplier, onClose, onSaved }) {
  const [terms, setTerms] = useState(supplier.payment_terms || '');
  const [method, setMethod] = useState(supplier.payment_method || '');
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await suppliersApi.updatePaymentTerms(supplier.supplierId, {
        payment_terms: terms,
        payment_method: method,
      });
      toast('תנאי התשלום עודכנו');
      onSaved({ payment_terms: terms, payment_method: method });
    } catch (err) {
      toast(err.response?.data?.error || 'שגיאה', 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      onClose={onClose}
      ariaLabel="ערוך תנאי תשלום"
      size={420}
      closeOnBackdrop={false}
    >
      <h2 className="modal-title">ערוך תנאי תשלום</h2>
      <div style={{ color: 'var(--ink-muted)', fontSize: '0.9rem', marginBottom: 12 }}>{supplier.supplierName}</div>
      <div className="form-group">
        <label>תנאי תשלום</label>
        <PaymentTermsInput value={terms} onChange={setTerms} />
      </div>
      <div className="form-group">
        <label>אמצעי תשלום</label>
        <select className="form-control" value={method} onChange={e => setMethod(e.target.value)}>
          <option value="">— ללא —</option>
          {PAYMENT_METHOD_OPTIONS.map(o => (
            <option key={o.value} value={o.value}>{o.value}</option>
          ))}
        </select>
      </div>
      <div className="modal-actions">
        <button className="btn btn-secondary" onClick={onClose}>ביטול</button>
        <button className="btn btn-success" disabled={saving} onClick={handleSave}>
          {saving ? 'שומר...' : 'שמור'}
        </button>
      </div>
    </Modal>
  );
}
